import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { IoChevronForward, IoChatbubblesOutline } from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import ReviewSpace from "@/components/ReviewSpace";
import { getCarnet, markCarnetResponsesSeen } from "@/lib/api";

const TABS = [
  { key: "all", label: "Toutes" },
  { key: "open", label: "En cours" },
  { key: "closed", label: "Terminées" },
];
const studentStatus = (e) => {
  if (e.review_closed) return { key: "closed", bg: "#e8f7ec", fg: "#1a7f37", label: "Terminée" };
  if (e.sensei_response) return { key: "open", bg: "color-mix(in srgb, var(--brand) 14%, transparent)", fg: RED, label: "En cours" };
  return { key: "open", bg: "#fff3e0", fg: "#b26a00", label: "En attente" };
};

export default function CarnetReviews() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("all");
  const [openId, setOpenId] = useState(params.get("open") || null);

  const load = async () => {
    try { const d = await getCarnet(); setData(d); }
    catch { toast.error("Chargement impossible"); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (openId) markCarnetResponsesSeen().catch(() => {});
  }, [openId]);

  if (!data) return <DojoShell crumbs={[{ label: "Mon Carnet", to: "/carnet" }, { label: "Mes relectures" }]}><p style={{ color: "var(--text-secondary)" }}>Chargement…</p></DojoShell>;

  const cats = data.categories || [];
  const catLabel = (id) => cats.find((c) => c.id === id)?.label || "Dossier";
  const reviews = (data.entries || [])
    .filter((e) => e.review_requested)
    .map((e) => ({ ...e, category_label: catLabel(e.section), _st: studentStatus(e) }))
    .sort((a, b) => (b.responded_at || b.created_at || "").localeCompare(a.responded_at || a.created_at || ""));

  const counts = { all: reviews.length, open: reviews.filter((r) => r._st.key === "open").length, closed: reviews.filter((r) => r._st.key === "closed").length };
  const grouped = tab === "all" ? reviews : reviews.filter((r) => r._st.key === tab);
  const opened = reviews.find((r) => r.id === openId) || null;

  const closeSpace = () => { setOpenId(null); params.delete("open"); setParams(params, { replace: true }); };
  const onChanged = () => load();

  return (
    <DojoShell crumbs={[{ label: "Mon Carnet", to: "/carnet" }, { label: "Mes relectures" }]}>
      {opened ? (
        <ReviewSpace review={opened} role="student" onBack={closeSpace} onChanged={onChanged} />
      ) : (
        <>
          <h1 style={sx.h1}>Mes relectures</h1>
          <p style={sx.subh}>Toutes tes demandes de relecture, passées et en cours. Ouvre-en une pour suivre l'échange avec le Sensei.</p>

          <div style={sx.tabs} data-testid="my-review-tabs">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`my-review-tab-${t.key}`}
                style={{ ...sx.tab, ...(tab === t.key ? sx.tabActive : {}) }}>
                {t.label}
                <span style={{ ...sx.tabCount, ...(tab === t.key ? sx.tabCountActive : {}) }}>{counts[t.key] || 0}</span>
              </button>
            ))}
          </div>

          {grouped.length === 0 ? (
            <div style={sx.empty} data-testid="my-reviews-empty">
              {tab === "closed" ? "Aucune relecture terminée pour l'instant." : "Tu n'as pas encore demandé de relecture. Coche « Demander une relecture » sur une entrée de ton carnet."}
            </div>
          ) : (
            <div style={sx.rows} data-testid="my-reviews-list">
              {grouped.map((r) => {
                const nb = (r.thread || []).length;
                const unseen = r.sensei_response && r.student_seen === false;
                return (
                  <button key={r.id} style={sx.row} onClick={() => setOpenId(r.id)} data-testid={`my-review-row-${r.id}`}>
                    {unseen && <span style={sx.dot} data-testid="my-review-unread-dot" />}
                    <span style={sx.rowIcon}><IoChatbubblesOutline size={20} /></span>
                    <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <div style={sx.rowTitle}>{r.title}</div>
                      <div style={sx.meta}>{r.category_label}{nb ? ` · ${nb} message${nb > 1 ? "s" : ""}` : " · en attente d'un retour"}</div>
                    </div>
                    <span style={{ ...sx.status, background: r._st.bg, color: r._st.fg }}>{r._st.label}</span>
                    <span style={sx.seeBtn} data-testid={`my-review-see-${r.id}`}>Voir la relecture <IoChevronForward size={14} /></span>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </DojoShell>
  );
}

const sx = {
  h1: { fontSize: 30, fontWeight: 900, color: "var(--text-primary)", fontFamily: "var(--font-display)", margin: 0 },
  subh: { color: "var(--text-secondary)", fontSize: 14, margin: "6px 0 20px", maxWidth: 640 },
  tabs: { display: "flex", gap: 8, marginBottom: 22, flexWrap: "wrap" },
  tab: { display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 16px", borderRadius: 999, border: "1.5px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-secondary)", fontWeight: 800, fontSize: 13.5, cursor: "pointer" },
  tabActive: { background: RED, color: "#fff", border: `1.5px solid ${RED}` },
  tabCount: { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 20, height: 20, padding: "0 6px", borderRadius: 999, background: "var(--bg-tertiary)", color: "var(--text-primary)", fontSize: 12, fontWeight: 900 },
  tabCountActive: { background: "rgba(255,255,255,0.25)", color: "#fff" },
  empty: { padding: "44px 20px", textAlign: "center", color: "var(--text-secondary)", border: "1px dashed var(--border-c)", borderRadius: 16 },
  rows: { display: "flex", flexDirection: "column", gap: 10 },
  row: { position: "relative", display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "14px 16px", borderRadius: 16, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)", cursor: "pointer" },
  dot: { position: "absolute", top: 12, left: 6, width: 8, height: 8, borderRadius: "50%", background: RED },
  rowIcon: { width: 44, height: 44, borderRadius: 12, background: "var(--bg-tertiary)", color: RED, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  rowTitle: { fontWeight: 800, color: "var(--text-primary)", fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  meta: { fontSize: 12.5, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  status: { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 11px", borderRadius: 999, fontSize: 11.5, fontWeight: 800, flexShrink: 0, whiteSpace: "nowrap" },
  seeBtn: { display: "inline-flex", alignItems: "center", gap: 4, color: RED, fontWeight: 800, fontSize: 13, flexShrink: 0 },
};
